import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  Refresh,
  MoreVert,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  Receipt,
  Business,
  Close,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import DataTable, { Column } from '../common/DataTable';
import ExpenseForm from './ExpenseForm';
import { expenseService, Expense, ExpenseFilters } from '../../services/expenseService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';

const ExpenseList: React.FC = () => {
  const dispatch = useAppDispatch();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<ExpenseFilters>({});
  const [formOpen, setFormOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuExpense, setMenuExpense] = useState<Expense | null>(null);

  const columns: Column[] = [
    {
      id: 'expenseDate',
      label: 'Date',
      format: (value: any, expense: Expense) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {dayjs(expense.expenseDate).format('MMM DD, YYYY')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dayjs(expense.createdAt).format('hh:mm A')}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      format: (value: any, expense: Expense) => (
        <Chip
          label={expense.category}
          size="small"
          variant="outlined"
          sx={{ borderColor: '#E8B4B8', color: '#E8B4B8' }}
        />
      ),
    },
    {
      id: 'description',
      label: 'Description',
      format: (value: any, expense: Expense) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {expense.description}
          </Typography>
          {expense.vendorName && (
            <Box display="flex" alignItems="center" mt={0.5}>
              <Business fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {expense.vendorName}
              </Typography>
            </Box>
          )}
        </Box>
      ),
    },
    {
      id: 'amount',
      label: 'Amount',
      format: (value: any, expense: Expense) => (
        <Box textAlign="right">
          <Typography variant="body2" fontWeight={700} color="error.main">
            ₹{expense.amount.toLocaleString()}
          </Typography>
          {expense.gstAmount && (
            <Typography variant="caption" color="text.secondary">
              GST: ₹{expense.gstAmount.toLocaleString()}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'paymentMethod',
      label: 'Payment Method',
      format: (value: any, expense: Expense) => (
        <Chip
          label={expense.paymentMethod}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      format: (value: any, expense: Expense) => (
        <Chip
          label={expense.status}
          size="small"
          color={
            expense.status === 'APPROVED'
              ? 'success'
              : expense.status === 'PENDING'
                ? 'warning'
                : 'error'
          }
          variant="outlined"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      format: (value: any, expense: Expense) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, expense)}
          size="small"
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        startDate: filters.startDate,
        endDate: filters.endDate,
      };

      const response = await expenseService.getExpenses(params);
      setExpenses(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch expenses',
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, [pagination.page, pagination.limit]);

  const handleFilterChange = (key: keyof ExpenseFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchExpenses();
  };

  const handleClearFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchExpenses();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, expense: Expense) => {
    setAnchorEl(event.currentTarget);
    setMenuExpense(expense);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuExpense(null);
  };

  const handleEdit = () => {
    setSelectedExpense(menuExpense);
    setFormOpen(true);
    handleMenuClose();
  };

  const handleView = () => {
    setSelectedExpense(menuExpense);
    setDetailsOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setExpenseToDelete(menuExpense);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!expenseToDelete) return;

    try {
      await expenseService.deleteExpense(expenseToDelete.id);
      dispatch(addNotification({
        type: 'success',
        message: 'Expense deleted successfully',
      }));
      fetchExpenses();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to delete expense',
      }));
    } finally {
      setDeleteConfirmOpen(false);
      setExpenseToDelete(null);
    }
  };

  const handleApprove = async () => {
    if (!menuExpense) return;

    try {
      await expenseService.approveExpense(menuExpense.id);
      dispatch(addNotification({
        type: 'success',
        message: 'Expense approved successfully',
      }));
      fetchExpenses();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to approve expense',
      }));
    } finally {
      handleMenuClose();
    }
  };

  const handleReject = async () => {
    if (!menuExpense) return;

    try {
      await expenseService.rejectExpense(menuExpense.id);
      dispatch(addNotification({
        type: 'success',
        message: 'Expense rejected successfully',
      }));
      fetchExpenses();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to reject expense',
      }));
    } finally {
      handleMenuClose();
    }
  };

  const handleFormSuccess = () => {
    fetchExpenses();
    setSelectedExpense(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Expense Management
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchExpenses}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedExpense(null);
                setFormOpen(true);
              }}
              sx={{
                bgcolor: '#E8B4B8',
                '&:hover': { bgcolor: '#D4969B' },
              }}
            >
              Add Expense
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Search expenses..."
                  value={(filters as any).search || ''}
                  onChange={(e) => handleFilterChange('search' as any, e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="APPROVED">Approved</MenuItem>
                    <MenuItem value="REJECTED">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={filters.paymentMethod || ''}
                    onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                    label="Payment Method"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="CARD">Card</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="ONLINE">Online</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate ? dayjs(filters.startDate) : null}
                  onChange={(date: any) => handleFilterChange('startDate' as any, date ? date.toISOString() : undefined)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate ? dayjs(filters.endDate) : null}
                  onChange={(date: any) => handleFilterChange('endDate' as any, date ? date.toISOString() : undefined)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Box display="flex" gap={1} height="100%" alignItems="center">
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    <FilterList />
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Expenses Table */}
        <DataTable
          columns={columns}
          rows={expenses}
          loading={loading}
          page={pagination.page - 1}
          rowsPerPage={pagination.limit}
          totalCount={pagination.total}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page: page + 1 }))}
          onRowsPerPageChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
        />

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleView}>
            <ListItemIcon>
              <Receipt fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          {menuExpense?.status === 'PENDING' && (
            <>
              <MenuItem onClick={handleApprove}>
                <ListItemIcon>
                  <CheckCircle fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText>Approve</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleReject}>
                <ListItemIcon>
                  <Cancel fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Reject</ListItemText>
              </MenuItem>
            </>
          )}
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Expense Form Dialog */}
        <ExpenseForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          expense={selectedExpense}
          onSuccess={handleFormSuccess}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this expense? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Expense Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Expense Details</Typography>
              <IconButton onClick={() => setDetailsOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedExpense && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Basic Information
                    </Typography>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Category
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedExpense.category}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedExpense.description}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={selectedExpense.status}
                        size="small"
                        color={
                          selectedExpense.status === 'APPROVED'
                            ? 'success'
                            : selectedExpense.status === 'PENDING'
                              ? 'warning'
                              : 'error'
                        }
                      />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Financial Details
                    </Typography>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="error.main">
                        ₹{selectedExpense.amount.toLocaleString()}
                      </Typography>
                    </Box>
                    {selectedExpense.gstAmount && (
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          GST Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          ₹{selectedExpense.gstAmount.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Chip
                        label={selectedExpense.paymentMethod}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Additional Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mb={2}>
                          <Typography variant="body2" color="text.secondary">
                            Expense Date
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {dayjs(selectedExpense.expenseDate).format('MMMM DD, YYYY')}
                          </Typography>
                        </Box>
                        {selectedExpense.receiptNumber && (
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              Receipt Number
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedExpense.receiptNumber}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {selectedExpense.vendorName && (
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              Vendor
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedExpense.vendorName}
                            </Typography>
                          </Box>
                        )}
                        {selectedExpense.creator && (
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              Created By
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedExpense.creator.firstName} {selectedExpense.creator.lastName}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ExpenseList;